/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, ReactNode } from "react"
import { Chip } from "./Chip"
import { ChipContainer } from "./ChipContainer"

type TChipGroupProps<T extends string | number> = {
  className?: string
  style?: CSSProperties
  size?: "small" | "medium" | "large"
  options?: {
    label: ReactNode
    value: T
    color?: string
    iconSrc?: string
    iconAlt?: string
  }[]
  value?: T[]
  onChange?: (value: T[]) => void
}

export const ChipGroup = <T extends string | number>({
  className,
  style,
  size,
  options = [],
  value = [],
  onChange
}: TChipGroupProps<T>) => {
  const currentOptions: TChipGroupProps<T>["options"] = [
    {
      label: "ALL",
      value: "" as any
    },
    ...options
  ]

  const getVariant = (chipValue: T, index: number) => {
    //all active
    if (value.length === 0 && value.length !== options.length)
      return index === 0 ? "filled" : "outlined"

    if (value.length === options.length)
      return index === 0 ? "filled" : "outlined"

    return value.includes(chipValue) ? "filled" : "outlined"
  }

  const handleChange = (newValue: T) => {
    if (newValue === "") return onChange?.(options.map(option => option.value))
    if (value.length === options.length) return onChange?.([newValue])

    onChange?.(
      value.includes(newValue)
        ? value.filter(v => v !== newValue)
        : [...value, newValue]
    )
  }

  return (
    <ChipContainer className={className} style={style}>
      {currentOptions.map(
        ({ label, value: chipValue, ...chipProps }, index) => (
          <Chip
            key={chipValue}
            size={size}
            variant={getVariant(chipValue, index)}
            onClick={() => handleChange(chipValue)}
            {...chipProps}
          >
            {label}
          </Chip>
        )
      )}
    </ChipContainer>
  )
}
